import React, { useState, useEffect } from 'react';
import { adminTestsClient } from '../../services/api';

function DataLoading() {
  const [tickerStats, setTickerStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTickerStats();
  }, []);

  const fetchTickerStats = async () => {
    try {
      const response = await adminTestsClient.get('/admin-tests/data/ticker-stats/');
      setTickerStats(response.data);
    } catch (err) {
      console.error('Failed to fetch ticker stats:', err);
      setError('Failed to load ticker statistics');
    }
  };

  const handleLoadTickers = async () => {
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await adminTestsClient.post('/admin-tests/data/load-tickers/');
      setMessage(`Success! Created: ${response.data.created}, Updated: ${response.data.updated}, Total: ${response.data.total_in_database}`);
      // Refresh stats after loading
      await fetchTickerStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tickers');
    } finally{
      setLoading(false);
    }
  };

  const handleClearTickers = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete ALL ticker data? This action cannot be undone!'
    );

    if (!confirmed) return;

    const doubleConfirm = window.prompt(
      'Type "DELETE_ALL_TICKERS" to confirm:'
    );

    if (doubleConfirm !== 'DELETE_ALL_TICKERS') {
      setError('Deletion cancelled - confirmation text did not match');
      return;
    }

    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await adminTestsClient.post('/admin-tests/data/clear-tickers/', {
        confirm: 'DELETE_ALL_TICKERS'
      });
      setMessage(response.data.message);
      await fetchTickerStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear tickers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-loading-container">
      <h2>Data Loading & Management</h2>
      <p className="description">
        Manage ticker database and other data loading operations for production and development environments.
      </p>

      {/* Ticker Statistics Card */}
      <div className="stats-card">
        <h3>Ticker Database Statistics</h3>
        {tickerStats ? (
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Tickers:</span>
              <span className={`stat-value ${tickerStats.total_tickers > 0 ? 'success' : 'warning'}`}>
                {tickerStats.total_tickers.toLocaleString()}
              </span>
            </div>

            <div className="stat-item">
              <span className="stat-label">Database Status:</span>
              <span className={`stat-value ${tickerStats.database_populated ? 'success' : 'error'}`}>
                {tickerStats.database_populated ? 'Populated' : 'Empty'}
              </span>
            </div>

            <div className="stat-item">
              <span className="stat-label">Ready for Testing:</span>
              <span className={`stat-value ${tickerStats.ready_for_testing ? 'success' : 'warning'}`}>
                {tickerStats.ready_for_testing ? 'Yes' : 'No'}
              </span>
            </div>

            <div className="stat-item full-width">
              <span className="stat-label">Major Tickers:</span>
              <div className="ticker-chips">
                {tickerStats.major_tickers.existing.map(ticker => (
                  <span key={ticker} className="chip success">{ticker} ✓</span>
                ))}
                {tickerStats.major_tickers.missing.map(ticker => (
                  <span key={ticker} className="chip error">{ticker} ✗</span>
                ))}
              </div>
            </div>

            {Object.keys(tickerStats.type_breakdown).length > 0 && (
              <div className="stat-item full-width">
                <span className="stat-label">Type Breakdown:</span>
                <div className="type-breakdown">
                  {Object.entries(tickerStats.type_breakdown).map(([type, count]) => (
                    <span key={type} className="type-item">
                      {type}: {count.toLocaleString()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="loading-placeholder">Loading statistics...</div>
        )}

        <button
          className="btn-secondary btn-sm"
          onClick={fetchTickerStats}
          disabled={loading}
        >
          🔄 Refresh Stats
        </button>
      </div>

      {/* Actions Card */}
      <div className="actions-card">
        <h3>Ticker Data Operations</h3>

        <div className="action-item">
          <div className="action-info">
            <h4>Load Ticker Data</h4>
            <p>
              Fetches all US stock tickers from EODHD API and loads them into the database.
              This operation will create new tickers and update existing ones.
            </p>
            <p className="note">
              <strong>Expected:</strong> ~7,000+ US stock tickers<br />
              <strong>Duration:</strong> 30-60 seconds
            </p>
          </div>
          <button
            className="btn-primary"
            onClick={handleLoadTickers}
            disabled={loading}
          >
            {loading ? '⏳ Loading...' : '📥 Load Tickers'}
          </button>
        </div>

        <div className="action-item danger-zone">
          <div className="action-info">
            <h4>⚠️ Clear All Ticker Data</h4>
            <p>
              Deletes all ticker data from the database. Use with caution!
              This action cannot be undone.
            </p>
            <p className="note">
              <strong>Use case:</strong> Reset database or clear corrupted data
            </p>
          </div>
          <button
            className="btn-danger"
            onClick={handleClearTickers}
            disabled={loading}
          >
            {loading ? '⏳ Clearing...' : '🗑️ Clear All Tickers'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="success-message">
          ✓ {message}
        </div>
      )}

      {error && (
        <div className="error-message">
          ✗ {error}
        </div>
      )}
    </div>
  );
}

export default DataLoading;
